// Story 16.7 — quién hizo qué. El admin ve todo (también logins y cuentas, con
// IPs); el owner de una organización, lo de las suyas. Lo más nuevo primero.

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useOrganizations } from '@/hooks/queries/use-organizations';
import { api } from '@/lib/api-client';
import {
  AUDIT_CATEGORY_LABELS,
  type AuditEntry,
  actionLabel,
  describeEntry,
} from '@/lib/audit-labels';

const PAGE_SIZE = 50;

const selectClass =
  'block h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

interface AuditResponse {
  data: AuditEntry[];
  pagination: { total: number; limit: number; offset: number };
}

export function AuditPage(): React.ReactElement {
  const [organizationId, setOrganizationId] = useState('');
  const [category, setCategory] = useState('');
  const [offset, setOffset] = useState(0);
  const organizations = useOrganizations();
  const manageable = (organizations.data ?? []).filter((o) =>
    o.capabilities.includes('manage_members'),
  );

  const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
  if (organizationId) params.set('organizationId', organizationId);
  if (category) params.set('action', category);
  const audit = useQuery({
    queryKey: ['audit', organizationId, category, offset],
    queryFn: () => api.getEnvelope<AuditResponse>(`/api/v1/audit?${params}`),
  });
  const total = audit.data?.pagination.total ?? 0;
  const rows = audit.data?.data ?? [];
  const showIp = rows.some((r) => 'ip' in r);

  const filter = (set: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    set(e.target.value);
    setOffset(0);
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Auditoría</h1>
        <p className="text-sm text-muted-foreground">
          Quién hizo qué: accesos, miembros, credenciales, deploys. No se borra con la retención de
          eventos.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label htmlFor="audit-org">Organización</Label>
          <select
            id="audit-org"
            className={selectClass}
            value={organizationId}
            onChange={filter(setOrganizationId)}
          >
            <option value="">Todas</option>
            {manageable.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="audit-category">Tipo</Label>
          <select
            id="audit-category"
            className={selectClass}
            value={category}
            onChange={filter(setCategory)}
          >
            <option value="">Todo</option>
            {Object.entries(AUDIT_CATEGORY_LABELS).map(([prefix, label]) => (
              <option key={prefix} value={prefix}>
                {label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {audit.isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : audit.isError ? (
        <p className="text-sm text-destructive">No se pudo cargar la auditoría.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No hay nada registrado con estos filtros.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Cuándo</th>
                <th className="px-3 py-2">Quién</th>
                <th className="px-3 py-2">Qué</th>
                <th className="px-3 py-2">Detalle</th>
                <th className="px-3 py-2">Organización</th>
                {showIp && <th className="px-3 py-2">IP</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-border/50">
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                    {new Date(r.at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">{r.actorEmail ?? '—'}</td>
                  <td className="px-3 py-2 font-medium">{actionLabel(r.action)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{describeEntry(r)}</td>
                  <td className="px-3 py-2">{r.organizationName ?? '—'}</td>
                  {showIp && (
                    <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                      {r.ip ?? '—'}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} de {total}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Más nuevos
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Más viejos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
